type ApiCacheStorage = 'none' | 'local'

export interface UseApiOptions {
  /**
   * Delay between two queued requests, in milliseconds.
   * The first request starts immediately.
   */
  delay?: number
  /**
   * Persist successful responses in localStorage.
   * Nuxt payload/static data are always used first.
   */
  storage?: ApiCacheStorage
  /** How long a localStorage entry stays valid, in milliseconds. */
  storageTtl?: number
  /** Maximum request duration, in milliseconds. */
  timeout?: number
  /**
   * Serialize requests through an app-level queue.
   * Defaults to client-side only so SSR requests can resolve in parallel.
   */
  queue?: boolean
  /** Forwarded to useAsyncData. */
  server?: boolean
  lazy?: boolean
  immediate?: boolean
}

interface StoredApiResponse {
  expiresAt: number
  data: unknown
}

// همه‌ی درخواست‌ها از پروکسی داخلی عبور می‌کنند: مرورگر اجازه‌ی ست‌کردن
// هدر Referer را ندارد و سرور بالادست بدون آن پاسخ درست نمی‌دهد.
const baseApi = '/api/portify'
// نسخه در پیشوند: کش‌های قدیمی که با referer اشتباه پر شده بودند بی‌اعتبار می‌شوند.
const storagePrefix = 'portify:api:v2:'

// A queue per Nuxt app keeps SSR requests isolated while serializing browser calls.
const requestQueues = new WeakMap<object, Promise<void>>()

const wait = (milliseconds: number) =>
  new Promise<void>(resolve => setTimeout(resolve, milliseconds))

async function request<T>(
  route: string,
  site: string,
  timeout: number,
  onSuccess: (data: T) => void
) {
  const data = await $fetch<T>(`${baseApi}/${route.replace(/^\/+/, '')}`, {
    method: 'GET',
    // پروکسی از این مقدار برای ساختن referer درست استفاده می‌کند.
    headers: { 'x-portify-site': site },
    timeout
  })

  onSuccess(data)
  return data
}

function enqueue<T>(app: object, task: () => Promise<T>, delay: number) {
  const previous = requestQueues.get(app)
  const run = (previous ?? Promise.resolve()).then(async () => {
    if (previous) await wait(delay)
    return task()
  })

  requestQueues.set(
    app,
    run.then(
      () => undefined,
      () => undefined
    )
  )

  return run
}

function readLocalCache<T>(key: string): T | undefined {
  if (!import.meta.client) return undefined

  try {
    const raw = localStorage.getItem(storagePrefix + key)
    if (!raw) return undefined

    const cached = JSON.parse(raw) as StoredApiResponse
    if (cached.expiresAt <= Date.now()) {
      localStorage.removeItem(storagePrefix + key)
      return undefined
    }

    return cached.data as T
  } catch {
    // A malformed cache must never prevent the API request.
    localStorage.removeItem(storagePrefix + key)
    return undefined
  }
}

function writeLocalCache(key: string, data: unknown, ttl: number) {
  if (!import.meta.client || data === null || data === undefined) return

  try {
    const value: StoredApiResponse = {
      expiresAt: Date.now() + ttl,
      data
    }
    localStorage.setItem(storagePrefix + key, JSON.stringify(value))
  } catch {
    // Storage can be unavailable or full; the in-memory/Nuxt response is still valid.
  }
}

function getNuxtCachedData<T>(key: string, nuxtApp: ReturnType<typeof useNuxtApp>) {
  const payloadData = nuxtApp.payload.data[key]
  if (payloadData !== undefined) return payloadData as T

  const staticData = nuxtApp.static.data[key]
  return staticData as T | undefined
}

export const useApi = <T = unknown>(
  route: string,
  key: string,
  options: UseApiOptions = {}
) => {
  const nuxtApp = useNuxtApp()
  // `xForwardedHost` لازم است: در SSR پشت nginx، هدر `Host` ممکن است آدرس
  // داخلی (مثل 127.0.0.1:3000) باشد و دامنه‌ی واقعی فقط در `X-Forwarded-Host`
  // بیاید. بدون این گزینه، همه‌ی دامنه‌ها یک referer اشتباه می‌فرستند.
  // بقیه‌ی کد (`pages/index.vue` و `pages/projects/[slug].vue`) هم همین را دارد.
  const requestUrl = useRequestURL({ xForwardedHost: true })
  const site = requestUrl.host.includes('localhost')
    ? 'fatemeh-nassari.ir'
    : requestUrl.host
  const delay = Math.max(0, options.delay ?? 250)
  const storage = options.storage ?? 'none'
  const storageTtl = Math.max(0, options.storageTtl ?? 5 * 60 * 1000)
  const timeout = Math.max(1000, options.timeout ?? 8000)
  const queued = options.queue ?? import.meta.client
  const fetchData = () =>
    request<T>(route, site, timeout, data => {
      if (storage === 'local') writeLocalCache(key, data, storageTtl)
    })

  const asyncData = useAsyncData<T | null>(
    key,
    () =>
      queued ? enqueue(nuxtApp, fetchData, delay) : fetchData(),
    {
      default: () => null,
      server: options.server,
      lazy: options.lazy,
      immediate: options.immediate,
      getCachedData: (cacheKey, app) => {
        const nuxtData = getNuxtCachedData<T | null>(cacheKey, app)
        if (nuxtData !== undefined) return nuxtData
        return storage === 'local' ? readLocalCache<T>(cacheKey) : undefined
      }
    }
  )

  // SSR responses are hydrated without running the handler again on the client.
  // Persist that hydrated value so the next client-side visit can use it instantly.
  if (storage === 'local' && import.meta.client) {
    watch(
      asyncData.data,
      data => writeLocalCache(key, data, storageTtl),
      { immediate: true }
    )
  }

  return asyncData
}
